/**
 * Typed API client. A token provider is injected by the auth layer so this
 * module stays free of MSAL (avoids a circular import). Every response shape is
 * typed against @cvs/shared plus a few API-only envelopes declared here.
 */
import type {
  AccessStatus,
  Badge,
  ClassComment,
  Course,
  EnhancementStatus,
  Enrollment,
  ModuleFeedback,
  InterviewQuestion,
  Batch,
  Chapter,
  DashboardSummary,
  GateResult,
  InterviewPackage,
  InterviewRequest,
  Lab,
  LabKind,
  Lesson,
  LessonType,
  Module,
  ModuleStatus,
  ModuleView,
  Program,
  Progress,
  Question,
  Quiz,
  QuizAttempt,
  QuizResult,
  Role,
  Section,
  Submission,
  SubmissionAttachment,
  SubmissionStatus,
  StudyQuestion,
  StudyQuestionPublic,
  StudyAttempt,
  User,
  BankQuestionPublic,
  BankAttempt,
  BankReveal,
  BankSessionState,
  BankDebrief,
} from '@cvs/shared';
import { env } from './env';

// ── API-only response envelopes ──────────────────────────────────────────────
export interface LearnerRow extends User {
  batchName: string | null;
  /** Course ids the learner is actively enrolled in. */
  courseIds: string[];
}

export interface AdminKpis {
  activeMembers: number;
  awaitingPayment: number;
  completionRate: number;
  watchHours: number;
  pendingReviews: number;
}
export interface PipelineRow {
  id: string;
  title: string;
  status: ModuleStatus;
  recorded: number;
  total: number;
  releaseWeek: number;
}
export interface GradingQueueRow {
  id: string;
  userName: string;
  labTitle: string;
  submittedAt: string;
}
export interface AdminOverview {
  kpis: AdminKpis;
  pipeline: PipelineRow[];
  gradingQueue: GradingQueueRow[];
}

export interface FeedbackClassRow {
  moduleId: string;
  moduleTitle: string;
  sectionTitle: string | null;
  count: number;
  avgRating: number | null;
  comments: { id: string; userName: string; rating: number; comment: string; createdAt: string }[];
  discussion: { id: string; userName: string; body: string; createdAt: string }[];
  status: EnhancementStatus;
  note: string;
}

export interface ActivityLabRow {
  labId: string;
  title: string;
  kind: LabKind;
  points: number;
  sectionTitle: string | null;
  completed: { userId: string; name: string; status: SubmissionStatus; grade: number | null }[];
  pending: { userId: string; name: string }[];
}
export interface ActivityQuizRow {
  quizId: string;
  title: string;
  moduleTitle: string | null;
  taken: { userId: string; name: string; bestPercent: number; attempts: number }[];
  notTaken: { userId: string; name: string }[];
}
export interface ActivityStudyRow {
  moduleId: string;
  title: string;
  sectionTitle: string | null;
  questionCount: number;
  practiced: { userId: string; name: string; questionsAnswered: number }[];
  notPracticed: { userId: string; name: string }[];
}
export interface ActivityReport {
  studentCount: number;
  labs: ActivityLabRow[];
  quizzes: ActivityQuizRow[];
  study: ActivityStudyRow[];
}

export interface StudentDetail {
  userId: string;
  name: string;
  email: string;
  materials: { moduleId: string; moduleTitle: string; sectionTitle: string | null; done: number; total: number; watchedPercent: number | null }[];
  quizzes: { quizId: string; title: string; moduleTitle: string | null; bestPercent: number; attempts: number; lastAt: string }[];
  labs: { labId: string; title: string; kind: LabKind; status: SubmissionStatus; grade: number | null; points: number; submittedAt: string }[];
  feedbackGiven: { moduleId: string; moduleTitle: string; rating: number; comment: string | null; createdAt: string }[];
  commentsPosted: { moduleId: string; moduleTitle: string; body: string; createdAt: string }[];
}

export interface StudentReportRow {
  userId: string;
  name: string;
  email: string;
  accessStatus: AccessStatus;
  batchName: string | null;
  materialsCompleted: number;
  materialsTotal: number;
  quizzesTaken: number;
  quizzesTotal: number;
  quizAvgPercent: number | null;
  labsSubmitted: number;
  labsGraded: number;
  labsAwaitingReview: number;
  labsTotal: number;
  labAvgPercent: number | null;
  /** Avg % of available recordings watched (furthest point reached); null if none. */
  watchedAvgPercent: number | null;
  recordingsTotal: number;
  lastActivityAt: string | null;
}

export interface ContentRow extends Module {
  recorded: number;
  total: number;
  types: Record<string, number>;
  unlockDate: string;
}

export interface SubmissionRow extends Submission {
  userName: string;
  labTitle: string;
  labPoints: number;
}

export interface AssessmentRow {
  quiz: Quiz;
  moduleTitle: string;
  questionCount: number;
  unlocked: boolean;
  bestPercent: number | null;
}

/** A class with interview-style practice questions, for the Practice tab. */
export interface PracticeClass {
  moduleId: string;
  title: string;
  sectionTitle: string | null;
  questionCount: number;
  answeredCount: number;
}

/**
 * The printable question pack. Assembled server-side because the bank answers
 * are gated: an answer is present only when this learner has already attempted
 * that question, so there is nothing to hide client-side.
 */
export interface PackQuizGroup {
  quizId: string;
  title: string;
  moduleTitle: string;
  questions: { id: string; prompt: string; options: string[]; correctIndex: number; explanation: string }[];
}
export interface PackPracticeGroup {
  moduleId: string;
  moduleTitle: string;
  sectionTitle: string | null;
  questions: { id: string; prompt: string; modelAnswer: string; keyPoints: string[]; keyPointCount: number }[];
}
export interface PackBankGroup {
  topic: string;
  questions: {
    id: string;
    question: string;
    kind: 'technical' | 'behavioural' | 'scenario';
    subTopic: string | null;
    keyPointCount: number;
    /** Null unless this learner has attempted the question, or nobody wrote one. */
    answer: string | null;
    /** The hint ladder — gated exactly like `answer`. */
    keyPoints: string[] | null;
    unlocked: boolean;
    hasAnswer: boolean;
  }[];
}
export interface QuestionPack {
  printedFor: string;
  courses: string[];
  quizzes: PackQuizGroup[];
  practice: PackPracticeGroup[];
  bank: PackBankGroup[];
  tally: { quiz: number; practice: number; bank: number; unlocked: number };
}

/** One interview question tied to a section, with the model answer to reveal. */
export interface SectionInterviewQuestion {
  question: string;
  howAsked: string;
  answer?: string;
}

/** A section's interview Q&A set, for the "Section Q&A" self-study flow. */
export interface SectionQA {
  sectionId: string;
  sectionTitle: string;
  courseId: string | null;
  courseTitle: string | null;
  order: number;
  questions: SectionInterviewQuestion[];
}

// ── Token injection ──────────────────────────────────────────────────────────
let tokenProvider: (() => Promise<string | null>) | null = null;
export function setTokenProvider(fn: () => Promise<string | null>): void {
  tokenProvider = fn;
}

// The course provider injects a getter for the current selection; it's read at
// fetch time (so a switch reflects immediately) and appended as ?courseId to the
// student list endpoints. Returns null for "all courses" (no scoping).
let courseIdProvider: (() => string | null) | null = null;
export function setCourseIdProvider(fn: () => string | null): void {
  courseIdProvider = fn;
}
function withCourse(path: string): string {
  const id = courseIdProvider ? courseIdProvider() : null;
  if (!id) return path;
  return `${path}${path.includes('?') ? '&' : '?'}courseId=${encodeURIComponent(id)}`;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function authHeaders(extra?: HeadersInit): Promise<Headers> {
  const headers = new Headers(extra);
  // In mock-auth mode, tell the API which seeded role to impersonate.
  if (env.useMockAuth) headers.set('x-cvs-mock-role', env.mockRole);
  const token = tokenProvider ? await tokenProvider() : null;
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return headers;
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = await authHeaders(init.headers);
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const res = await fetch(`${env.apiBase}${path}`, { ...init, headers });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch {
      /* non-JSON error */
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const post = (path: string, body?: unknown, method = 'POST') =>
  apiFetch(path, { method, body: body === undefined ? undefined : JSON.stringify(body) });

export const api = {
  // Identity
  me: () => apiFetch<{ user: User; gate: GateResult }>('/users/me'),
  /** Set the learner's own study goal (lessons per week). */
  setStudyGoal: (weeklyGoal: number) => post('/users/me/goal', { weeklyGoal }, 'PATCH') as Promise<User>,

  /** Courses the current user can see (enrolled courses; instructors see all). */
  courses: () => apiFetch<Course[]>('/courses'),

  // Student curriculum
  program: () =>
    apiFetch<{ batch: Batch | null; sections: Section[]; modules: ModuleView[]; projects: Lab[] }>(
      withCourse('/program'),
    ),
  moduleLessons: (moduleId: string) =>
    apiFetch<{ module: Module; lessons: Lesson[]; quizId: string | null; labs: Lab[] }>(
      `/modules/${moduleId}/lessons`,
    ),
  lesson: (lessonId: string) =>
    apiFetch<{ lesson: Lesson; module: Module | null; progress: Progress | null }>(`/lessons/${lessonId}`),
  /** Short-lived playable URL for a lesson's file. `kind` says how to render it. */
  lessonFile: (lessonId: string) =>
    apiFetch<{ url: string | null; kind?: 'video' | 'file' | 'embed' | 'none' }>(`/lessons/${lessonId}/file`),

  // Progress
  progress: () => apiFetch<Progress[]>('/progress'),
  saveProgress: (body: { lessonId: string; completed?: boolean; lastPositionSec?: number; watchedSec?: number }) =>
    post('/progress', body) as Promise<Progress>,

  // Quizzes
  assessments: () => apiFetch<AssessmentRow[]>(withCourse('/assessments')),
  /** Classes with interview-style practice questions + the learner's saved-answer counts. */
  practice: () => apiFetch<PracticeClass[]>(withCourse('/practice')),
  quiz: (quizId: string) => apiFetch<{ quiz: Quiz; questions: Question[] }>(`/quizzes/${quizId}`),
  /** Every question this learner may see, in one printable payload. */
  questionPack: () => apiFetch<QuestionPack>(withCourse('/question-pack')),
  /** A read SAS for one piece of evidence, minted now rather than at page load. */
  gradingAttachmentUrl: (submissionId: string, index: number) =>
    apiFetch<{ url: string }>(`/studio/grading/${submissionId}/attachments/${index}`),
  submitQuiz: (quizId: string, answers: number[]) =>
    post(`/quizzes/${quizId}/attempts`, { answers }) as Promise<{ result: QuizResult; attempt: QuizAttempt }>,

  // Interview question bank. The list deliberately carries NO answers — the reveal
  // endpoint returns them, and only once an attempt has been saved server-side.
  bank: (courseId?: string | null) =>
    apiFetch<{ questions: BankQuestionPublic[]; attemptedIds: string[]; gradingEnabled: boolean }>(
      `/bank${courseId ? `?courseId=${encodeURIComponent(courseId)}` : ''}`,
    ),
  saveBankAnswer: (questionId: string, body: { answer: string; hintLevel?: number; mode?: 'drill' | 'interview' }) =>
    post(`/bank/${questionId}/attempts`, body) as Promise<BankAttempt>,
  revealBankAnswer: (questionId: string) => post(`/bank/${questionId}/reveal`, {}) as Promise<BankReveal>,
  bankHint: (questionId: string, level: number) =>
    post(`/bank/${questionId}/hint`, { level }) as Promise<{ level: number; hint: string | null; maxLevel: number }>,

  // Mock interview sessions. Note what is absent: there is no "list the questions" call.
  // The server hands over one question at a time, because a running order the client can
  // read is a reading list rather than an interview.
  startBankSession: (body: { scope: 'topic' | 'position'; scopeValue: string; length: number }) =>
    post('/bank/sessions', body) as Promise<BankSessionState>,
  answerBankSession: (sessionId: string, body: { answer: string; isFollowUp?: boolean }) =>
    post(`/bank/sessions/${sessionId}/answer`, body) as Promise<BankSessionState>,
  bankSession: (sessionId: string) =>
    apiFetch<BankSessionState | { sessionId: string; status: 'complete'; debrief: BankDebrief }>(`/bank/sessions/${sessionId}`),

  // Study Q&A (interview-style practice, AI-judged)
  studyQuestions: (moduleId: string) =>
    apiFetch<{ questions: StudyQuestionPublic[]; attempts: StudyAttempt[] }>(`/modules/${moduleId}/study-questions`),
  submitStudyAnswer: (questionId: string, answer: string) =>
    post(`/study-questions/${questionId}/attempts`, { answer }) as Promise<{ attempt: StudyAttempt }>,

  // Dashboard
  dashboard: () =>
    apiFetch<{ summary: DashboardSummary | null; badges: Badge[]; continueLesson: Lesson | null }>(
      withCourse('/dashboard'),
    ),

  // Labs
  labs: () => apiFetch<{ labs: Lab[]; submissions: Submission[] }>(withCourse('/labs')),
  /** How to present a lab's guide by type: 'pdf' (render `url` inline) | 'html'
   *  (render `html` in a sandboxed frame) | 'link' (open in a tab) | 'none'. */
  labGuide: (labId: string) =>
    apiFetch<{ url?: string | null; html?: string | null; kind: 'pdf' | 'html' | 'link' | 'none' }>(
      `/labs/${labId}/guide`,
    ),
  uploadSas: (fileName: string, contentType?: string) =>
    post('/uploads/sas', { fileName, contentType }) as Promise<{ uploadUrl: string | null; fileUrl: string }>,
  submitLab: (labId: string, attachments: SubmissionAttachment[]) =>
    post(`/labs/${labId}/submissions`, { attachments }) as Promise<Submission>,

  // Class engagement — feedback (own rating) + discussion comments
  myFeedback: (moduleId: string) => apiFetch<{ mine: ModuleFeedback | null }>(`/modules/${moduleId}/feedback`),
  saveFeedback: (moduleId: string, rating: number, comment: string) =>
    post(`/modules/${moduleId}/feedback`, { rating, comment }, 'PUT') as Promise<ModuleFeedback>,
  comments: (moduleId: string) => apiFetch<ClassComment[]>(`/modules/${moduleId}/comments`),
  postComment: (moduleId: string, body: string) =>
    post(`/modules/${moduleId}/comments`, { body }) as Promise<ClassComment>,
  deleteComment: (id: string) => post(`/comments/${id}`, undefined, 'DELETE') as Promise<{ deleted: string }>,

  // Interview prep
  interviewPackages: () => apiFetch<InterviewPackage[]>('/interview'),
  /** Sections (across enrolled courses) with interview Q&A, for the Section Q&A flow. */
  sectionQuestions: () => apiFetch<SectionQA[]>('/section-questions'),
  interviewPackage: (id: string) => apiFetch<InterviewPackage>(`/interview/${id}`),
  /** Returns the raw streaming Response; read response.body for incremental text. */
  interviewChat: async (body: InterviewRequest): Promise<Response> => {
    const headers = await authHeaders({ 'Content-Type': 'application/json' });
    return fetch(`${env.apiBase}/interview/chat`, { method: 'POST', headers, body: JSON.stringify(body) });
  },

  // Instructor Studio
  admin: {
    overview: () => apiFetch<AdminOverview>('/studio/overview'),
    /** Per-student accomplishment across materials, quizzes and labs/projects. */
    studentReport: (courseId?: string | null) =>
      apiFetch<{ students: StudentReportRow[] }>(
        `/studio/reports/students${courseId ? `?courseId=${encodeURIComponent(courseId)}` : ''}`,
      ),
    /** One student's full activity: materials, quizzes, labs, feedback + discussion. */
    studentDetail: (userId: string) => apiFetch<StudentDetail>(`/studio/reports/students/${userId}`),
    /** Per-activity completion: who has (and hasn't) done each lab and quiz. */
    activityReport: (courseId?: string | null) =>
      apiFetch<ActivityReport>(
        `/studio/reports/activities${courseId ? `?courseId=${encodeURIComponent(courseId)}` : ''}`,
      ),
    /** Every class ranked by rating, with comments + discussion + enhancement status/note. */
    feedbackOverview: (courseId?: string | null) =>
      apiFetch<{ classes: FeedbackClassRow[] }>(
        `/studio/feedback/overview${courseId ? `?courseId=${encodeURIComponent(courseId)}` : ''}`,
      ),
    /** Set a class's enhancement status/note (from the feedback panel). */
    setEnhancement: (moduleId: string, patch: { status?: EnhancementStatus; note?: string }) =>
      post(`/studio/modules/${moduleId}/enhancement`, patch, 'PUT') as Promise<unknown>,
    learners: () =>
      apiFetch<{ learners: LearnerRow[]; batches: Batch[]; courses: Course[] }>('/studio/learners'),
    addLearner: (body: { name: string; email: string; role?: Role; batchId?: string | null; courseIds?: string[] }) =>
      post('/studio/learners', body) as Promise<User & { courseIds: string[] }>,
    programs: () => apiFetch<Program[]>('/studio/programs'),
    createProgram: (body: { name: string; description?: string | null }) =>
      post('/studio/programs', body) as Promise<Program>,
    updateProgram: (id: string, patch: Partial<{ name: string; description: string | null; order: number }>) =>
      post(`/studio/programs/${id}`, patch, 'PATCH') as Promise<Program>,
    courses: () => apiFetch<Course[]>('/studio/courses'),
    createCourse: (body: {
      title: string;
      description?: string | null;
      programId?: string | null;
      weeks?: number | null;
      startDate?: string | null;
      status?: Course['status'];
    }) => post('/studio/courses', body) as Promise<Course>,
    updateCourse: (
      id: string,
      patch: Partial<{
        title: string;
        description: string | null;
        programId: string | null;
        weeks: number | null;
        startDate: string | null;
        status: Course['status'];
        order: number;
      }>,
    ) => post(`/studio/courses/${id}`, patch, 'PATCH') as Promise<Course>,
    // Both report `changed`, so a bulk caller can say what it actually did rather than
    // counting the requests it sent.
    enroll: (userId: string, courseId: string) =>
      post(`/studio/learners/${userId}/enrollments`, { courseId }) as Promise<Enrollment & { changed: boolean }>,
    unenroll: (userId: string, courseId: string) =>
      post(`/studio/learners/${userId}/enrollments/${courseId}`, undefined, 'DELETE') as Promise<{ revoked: string; changed: boolean }>,
    setAccess: (userId: string, status: AccessStatus) =>
      post(`/studio/learners/${userId}/access`, { status }, 'PATCH') as Promise<User>,
    setRole: (userId: string, role: Role) =>
      post(`/studio/learners/${userId}/role`, { role }, 'PATCH') as Promise<User>,
    deleteLearner: (userId: string) =>
      post(`/studio/learners/${userId}`, undefined, 'DELETE') as Promise<{ deleted: string }>,
    content: () =>
      apiFetch<{ sections: Section[]; modules: ContentRow[]; labs: Lab[]; batchStartDate: string }>('/studio/content'),
    /** Instructor SAS to upload course content (slides/resources) to private storage. */
    uploadContentSas: (fileName: string, contentType?: string) =>
      post('/studio/uploads/sas', { fileName, contentType }) as Promise<{ uploadUrl: string | null; fileUrl: string }>,
    grading: () => apiFetch<{ queue: SubmissionRow[]; graded: SubmissionRow[] }>('/studio/grading'),
    grade: (submissionId: string, grade: number, feedback: string) =>
      post(`/studio/grading/${submissionId}`, { grade, feedback }) as Promise<Submission>,

    // Content editor — sections
    createSection: (body: { title: string; order?: number; courseId?: string | null }) =>
      post('/studio/sections', body) as Promise<Section>,
    updateSection: (
      id: string,
      patch: Partial<{ title: string; order: number; interviewQuestions: InterviewQuestion[]; courseId: string | null }>,
    ) => post(`/studio/sections/${id}`, patch, 'PATCH') as Promise<Section>,
    /** AI-draft interview questions (+ model answers) for a section (for review). */
    generateInterviewQuestions: (sectionId: string) =>
      post(`/studio/sections/${sectionId}/interview/generate`) as Promise<{ questions: InterviewQuestion[] }>,
    /** AI-draft model answers for the given questions; returns the list with answers filled. */
    generateInterviewAnswers: (sectionId: string, questions: InterviewQuestion[]) =>
      post(`/studio/sections/${sectionId}/interview/answers`, { questions }) as Promise<{ questions: InterviewQuestion[] }>,
    /** All feedback for a class + its average (instructor view). */
    moduleFeedback: (moduleId: string) =>
      apiFetch<{ feedback: ModuleFeedback[]; count: number; average: number }>(
        `/studio/modules/${moduleId}/feedback`,
      ),
    deleteSection: (id: string) => post(`/studio/sections/${id}`, undefined, 'DELETE') as Promise<{ deleted: string }>,
    reorderSections: (ids: string[]) =>
      post('/studio/reorder-sections', { ids }) as Promise<{ ok: boolean; count: number }>,

    // Content editor — classes
    createModule: (body: {
      title: string;
      topics?: string[];
      learningGoals?: string[];
      releaseWeek?: number;
      status?: ModuleStatus;
      sectionId?: string | null;
    }) => post('/studio/modules', body) as Promise<Module>,
    updateModule: (
      id: string,
      patch: Partial<{
        title: string;
        topics: string[];
        learningGoals: string[];
        releaseWeek: number;
        status: ModuleStatus;
        order: number;
        sectionId: string | null;
      }>,
    ) => post(`/studio/modules/${id}`, patch, 'PATCH') as Promise<Module>,
    deleteModule: (id: string) => post(`/studio/modules/${id}`, undefined, 'DELETE') as Promise<{ deleted: string }>,
    reorderModules: (ids: string[]) =>
      post('/studio/reorder-modules', { ids }) as Promise<{ ok: boolean; count: number }>,

    // Content editor — class materials (recording/slides/transcript/summary)
    setMaterial: (
      classId: string,
      type: LessonType,
      body: { streamUrl?: string | null; textContent?: string | null; durationSec?: number; chapters?: Chapter[] | null },
    ) => post(`/studio/classes/${classId}/materials/${type}`, body, 'PUT') as Promise<Lesson>,
    /** AI-generate recording chapters from the class transcript (for review). */
    generateChapters: (classId: string) =>
      post(`/studio/modules/${classId}/chapters/generate`) as Promise<Chapter[]>,
    /** Pull the transcript .docx Teams saved next to the SharePoint recording. */
    fetchTranscript: (classId: string) =>
      post(`/studio/modules/${classId}/transcript/fetch`) as Promise<{ text: string; sourceName: string }>,
    /** AI-generate a class recap/summary from the transcript (for review). */
    generateSummary: (classId: string) =>
      post(`/studio/modules/${classId}/summary/generate`) as Promise<{ text: string }>,
    /** AI-generate structured study notes from the transcript (for review). */
    generateNotes: (classId: string) =>
      post(`/studio/modules/${classId}/notes/generate`) as Promise<{ text: string }>,
    deleteMaterial: (classId: string, type: LessonType) =>
      post(`/studio/classes/${classId}/materials/${type}`, undefined, 'DELETE') as Promise<{ deleted: string }>,
    createLesson: (
      moduleId: string,
      body: { title: string; type?: LessonType; streamUrl?: string | null; durationSec?: number },
    ) => post(`/studio/modules/${moduleId}/lessons`, body) as Promise<Lesson>,
    updateLesson: (
      id: string,
      patch: Partial<{ title: string; type: LessonType; streamUrl: string | null; durationSec: number; order: number }>,
    ) => post(`/studio/lessons/${id}`, patch, 'PATCH') as Promise<Lesson>,
    deleteLesson: (id: string) => post(`/studio/lessons/${id}`, undefined, 'DELETE') as Promise<{ deleted: string }>,

    // Labs & projects
    createLab: (body: {
      title: string;
      points?: number;
      env?: string;
      instructions?: string;
      resourceUrl?: string | null;
      guideHtml?: string | null;
      answerHtml?: string | null;
      kind?: LabKind;
      moduleId?: string | null;
      sectionId?: string | null;
    }) => post('/studio/labs', body) as Promise<Lab>,
    updateLab: (
      id: string,
      patch: Partial<{
        title: string;
        points: number;
        env: string;
        instructions: string;
        resourceUrl: string | null;
        guideHtml: string | null;
        answerHtml: string | null;
        kind: LabKind;
        moduleId: string | null;
        sectionId: string | null;
      }>,
    ) => post(`/studio/labs/${id}`, patch, 'PATCH') as Promise<Lab>,
    deleteLab: (id: string) => post(`/studio/labs/${id}`, undefined, 'DELETE') as Promise<{ deleted: string }>,

    // Quizzes + questions
    createQuiz: (moduleId: string, title?: string) =>
      post(`/studio/modules/${moduleId}/quiz`, { title }) as Promise<Quiz>,
    deleteQuiz: (id: string) => post(`/studio/quizzes/${id}`, undefined, 'DELETE') as Promise<{ deleted: string }>,
    createQuestion: (
      quizId: string,
      body: { prompt: string; options: string[]; correctIndex: number; explanation?: string },
    ) => post(`/studio/quizzes/${quizId}/questions`, body) as Promise<Question>,
    updateQuestion: (
      id: string,
      patch: Partial<{ prompt: string; options: string[]; correctIndex: number; explanation: string }>,
    ) => post(`/studio/questions/${id}`, patch, 'PATCH') as Promise<Question>,
    deleteQuestion: (id: string) => post(`/studio/questions/${id}`, undefined, 'DELETE') as Promise<{ deleted: string }>,
    generateQuestions: (quizId: string, body: { topic?: string; count?: number; useTranscript?: boolean }) =>
      post(`/studio/quizzes/${quizId}/generate`, body) as Promise<Question[]>,

    // Study Q&A (interview-style practice)
    studyQuestions: (moduleId: string) => apiFetch<StudyQuestion[]>(`/studio/modules/${moduleId}/study-questions`),
    createStudyQuestion: (moduleId: string, body: { prompt: string; modelAnswer?: string; keyPoints?: string[] }) =>
      post(`/studio/modules/${moduleId}/study-questions`, body) as Promise<StudyQuestion>,
    generateStudyQuestions: (moduleId: string, count?: number) =>
      post(`/studio/modules/${moduleId}/study-questions/generate`, { count }) as Promise<StudyQuestion[]>,
    updateStudyQuestion: (
      id: string,
      patch: Partial<{ prompt: string; modelAnswer: string; keyPoints: string[]; order: number }>,
    ) => post(`/studio/study-questions/${id}`, patch, 'PATCH') as Promise<StudyQuestion>,
    deleteStudyQuestion: (id: string) =>
      post(`/studio/study-questions/${id}`, undefined, 'DELETE') as Promise<{ deleted: string }>,

    // Interview package
    createInterview: (body?: { role?: string; company?: string }) =>
      post('/studio/interview', body ?? {}) as Promise<InterviewPackage>,
    updateInterview: (
      id: string,
      patch: Partial<Pick<InterviewPackage, 'role' | 'company' | 'studyGuide' | 'starQA' | 'projects'>>,
    ) => post(`/studio/interview/${id}`, patch, 'PATCH') as Promise<InterviewPackage>,
    deleteInterview: (id: string) => post(`/studio/interview/${id}`, undefined, 'DELETE') as Promise<{ deleted: string }>,
  },
};

/** Read a fetch Response body stream as text chunks. */
export async function* readTextStream(res: Response): AsyncGenerator<string> {
  if (!res.body) return;
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    yield decoder.decode(value, { stream: true });
  }
}
